import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Section, StatCard, Btn } from '@/components/db/parts';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import { fetchIngestInfo, ingestSyncFn, ingestVacuumFn, ingestRefreshMvFn } from '@/api/endpoints';
import type { IngestInfo, IngestSyncInput, IngestMaintInput } from '@/api/types';

function fmtTime(at?: string | number): string {
  if (!at) return '—';
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? String(at) : d.toLocaleTimeString('fr-FR');
}

function workerTone(etat: string): string {
  if (etat === 'RUNNING') return 'badge-blue';
  if (etat === 'AVAILABLE') return 'badge-slate';
  return 'badge-amber';
}

export default function Ingest({ device }: { device: Device }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superAdmin';
  const toast = useToast();
  const qc = useQueryClient();
  const refreshInfo = () => qc.invalidateQueries({ queryKey: ['ingest', 'info'] });

  const { data: info, isLoading, isError } = useQuery<IngestInfo>({
    queryKey: ['ingest', 'info'],
    queryFn: fetchIngestInfo,
    refetchInterval: 5000,
  });

  // ── action option state
  const [syncMeteor, setSyncMeteor] = useState('');
  const [syncFrom, setSyncFrom] = useState('');
  const [syncTo, setSyncTo] = useState('');
  const [syncBoost, setSyncBoost] = useState(false);
  const [vacFull, setVacFull] = useState(false);
  const [vacBoost, setVacBoost] = useState(false);
  const [mvFull, setMvFull] = useState(false);
  const [mvBoost, setMvBoost] = useState(false);

  const onQueued = (msg: string) => { toast.push(msg, 'success'); refreshInfo(); };

  const syncMut = useConfirmedMutation(
    (input: IngestSyncInput) => ingestSyncFn(input),
    {
      title: 'Lancer la synchronisation',
      message: 'Mettre la synchronisation en file ingest ?',
      onSuccess: (r) => onQueued((r as { message?: string }).message ?? 'Synchronisation mise en file'),
    },
  );
  const vacuumMut = useConfirmedMutation(
    (input: IngestMaintInput) => ingestVacuumFn(input),
    {
      title: 'VACUUM',
      message: 'Mettre un VACUUM en file ingest ?',
      destructive: true,
      onSuccess: (r) => onQueued((r as { message?: string }).message ?? 'VACUUM mis en file'),
    },
  );
  const mvMut = useConfirmedMutation(
    (input: IngestMaintInput) => ingestRefreshMvFn(input),
    {
      title: 'Refresh Materialized Views',
      message: 'Mettre un refresh des CAGGs en file ingest ?',
      onSuccess: (r) => onQueued((r as { message?: string }).message ?? 'Refresh MV mis en file'),
    },
  );

  return (
    <PageShell device={device} title="Service de synchronisation" breadcrumb="Services">
      <div className="p-4 md:p-6 space-y-4">

        {/* ── État + queue temps réel ── */}
        <Section title="État (temps réel · 5 s)">
          {isError ? (
            <div className="text-sm text-destructive">Service ingest injoignable (502). Réessai automatique…</div>
          ) : isLoading || !info ? (
            <div className="text-sm text-muted-foreground">Chargement…</div>
          ) : !info.queue ? (
            // Corps 200 mais incomplet : service en cours de démarrage ou de
            // redéploiement. Dégrader ici plutôt que de laisser l'accès à
            // `queue.taille` faire tomber toute l'application.
            <div className="text-sm text-amber-600 dark:text-amber-500">
              Snapshot incomplet — le service ingest répond mais n'expose pas encore sa queue
              (démarrage ou redéploiement en cours). Réessai automatique…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Queue" value={info.queue.taille} sub={info.queue.bloquee ? 'en pause' : 'active'} tone={info.queue.bloquee ? 'warn' : 'primary'} />
                <StatCard label="JSON en attente" value={info.queue.json.total} sub={`${info.queue.json.parStation.length} stations`} />
                <div className="bg-card border border-border rounded-lg p-3">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Worker</div>
                  {info.worker ? (
                    <span className={`${workerTone(info.worker.etat)} px-1.5 py-0.5 rounded text-[11px] font-medium font-mono inline-block mt-1`}>{info.worker.etat}</span>
                  ) : (
                    // /ready n'expose pas l'état worker : on le déduit de la queue.
                    <span className="badge-slate px-1.5 py-0.5 rounded text-[11px] font-medium font-mono inline-block mt-1">
                      {info.queue.jobEnCours ? 'RUNNING' : 'AVAILABLE'}
                    </span>
                  )}
                </div>
                <StatCard
                  label="DB"
                  value={info.db ? (info.db.ok ? `${info.db.latencyMs} ms` : 'KO') : '—'}
                  tone={info.db?.ok ? 'success' : 'warn'}
                  sub={info.db ? (info.db.ok ? 'latence' : info.db.error ?? 'erreur') : 'non renseigné'}
                />
              </div>

              <div className="text-sm">
                <span className="text-muted-foreground">Job en cours : </span>
                {info.queue.jobEnCours ? (
                  <span className="font-mono">{info.queue.jobEnCours.type} · {info.queue.jobEnCours.meteor || '—'} <span className="text-muted-foreground">({fmtTime(info.queue.jobEnCours.ajouteLe)})</span></span>
                ) : (
                  <span className="text-muted-foreground">aucun</span>
                )}
              </div>

              {info.queue.autres.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">type</th>
                        <th className="text-left px-3 py-2">meteor</th>
                        <th className="text-left px-3 py-2">priorité</th>
                        <th className="text-left px-3 py-2">ajouté</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {info.queue.autres.map((j, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono text-xs">{j.type}</td>
                          <td className="px-3 py-2 font-mono text-xs">{j.meteor || '—'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{j.priorite ?? '—'}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground font-mono">{fmtTime(j.ajouteLe)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {info.queue.json.parStation.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  JSON par station : {info.queue.json.parStation.map((s) => `${s.meteor} (${s.nb})`).join(' · ')}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── Actions ── */}
        <Section title="Actions">
          <div className="space-y-4">

            {/* sync */}
            <div className="flex flex-wrap items-end gap-2">
              <Field label="meteor (vide = toutes)">
                <input value={syncMeteor} onChange={(e) => setSyncMeteor(e.target.value)} placeholder="ex: MTG320"
                  className="h-9 px-2 rounded-md border border-input bg-background text-sm font-mono w-36" />
              </Field>
              <Field label="du">
                <input type="datetime-local" value={syncFrom} onChange={(e) => setSyncFrom(e.target.value)}
                  className="h-9 px-2 rounded-md border border-input bg-background text-sm" />
              </Field>
              <Field label="au">
                <input type="datetime-local" value={syncTo} onChange={(e) => setSyncTo(e.target.value)}
                  className="h-9 px-2 rounded-md border border-input bg-background text-sm" />
              </Field>
              <Chk label="boost" v={syncBoost} onChange={setSyncBoost} />
              <Btn variant="primary" disabled={syncMut.isPending}
                onClick={() => syncMut.mutate({
                  meteor: syncMeteor.trim() || undefined,
                  dateDebut: syncFrom || undefined,
                  dateFin: syncTo || undefined,
                  boost: syncBoost,
                })}>
                Sync
              </Btn>
            </div>

            {/* vacuum */}
            <div className="flex flex-wrap items-end gap-2">
              <span className="text-sm font-medium w-20">vacuum</span>
              <Chk label="full (superAdmin)" v={vacFull} onChange={setVacFull} disabled={!isSuperAdmin} />
              <Chk label="boost" v={vacBoost} onChange={setVacBoost} />
              <Btn variant="default" disabled={vacuumMut.isPending}
                onClick={() => vacuumMut.mutate({ full: vacFull, boost: vacBoost })}>
                VACUUM
              </Btn>
            </div>

            {/* refreshMV */}
            <div className="flex flex-wrap items-end gap-2">
              <span className="text-sm font-medium w-20">refreshMV</span>
              <Chk label="full recompute (superAdmin)" v={mvFull} onChange={setMvFull} disabled={!isSuperAdmin} />
              <Chk label="boost" v={mvBoost} onChange={setMvBoost} />
              <Btn variant="default" disabled={mvMut.isPending}
                onClick={() => mvMut.mutate({ full: mvFull, boost: mvBoost })}>
                Refresh MV
              </Btn>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Toutes ces actions sont mises dans la file ingest node_climato (sérialisée avec l'ingestion).
              <code className="font-mono"> boost</code> = priorité maximale. <code className="font-mono">full</code> = base entière / recompute total (superAdmin).
            </p>
          </div>
        </Section>

      </div>
    </PageShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</span>
      {children}
    </label>
  );
}

function Chk({ label, v, onChange, disabled }: { label: string; v: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={`flex items-center gap-1.5 h-9 px-2 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={v} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      <span>{label}</span>
    </label>
  );
}
